/**
 * UI state resolver for before/after instrumentation capture.
 * Components must render solely from CaptureUiState — never hardcode transitions.
 */

export type CaptureUiState =
  | 'DISABLED'
  | 'CAPTURE_PENDING'
  | 'WAITING_FOR_PR'
  | 'READY_TO_COMPARE'
  /** Repo was already instrumented before onboarding — no PR raised by us. */
  | 'ALREADY_INSTRUMENTED'
  /** Instrumentation job failed — no PR could be raised. */
  | 'INSTRUMENTATION_FAILED'
  | 'COMPARING'
  | 'PASSED'
  | 'FAILED'
  | 'HAS_DIFFERENCES'
  | 'CAPTURE_FAILED'
  | 'AFTER_CAPTURE_FAILED'
  | 'BEFORE_CAPTURE_FAILED'
  | 'COMPARE_FAILED';

export interface CaptureSummaryRepo {
  captureEnabled?: boolean;
  captureStatus?: string | null;
  comparisonStatus?: string | null;
  /** PASS | FAIL | UNKNOWN — from repository summary (list view). */
  comparisonResult?: string | null;
  prStatus?: string | null;
  captureId?: string | null;
  captureOptedInBy?: string | null;
  instrumentationPr?: {
    status?: string | null;
    prId?: number | null;
    prUrl?: string | null;
    prMerged?: boolean | null;
  } | null;
}

export interface CaptureDocLite {
  before?: { status?: string | null };
  after?: { status?: string | null };
  comparison?: {
    status?: string | null;
    result?: string | null;
    hasDifferences?: boolean | null;
  };
}

export function getRepositoryCaptureState(
  repository?: CaptureSummaryRepo | null,
  capture?: CaptureDocLite | null,
): CaptureUiState {
  if (!repository?.captureEnabled) return 'DISABLED';

  const repoCapture = (repository.captureStatus || '').toUpperCase();
  // Summary-only list view may only have captureStatus (no full capture doc).
  let beforeStatus = (capture?.before?.status || '').toUpperCase();
  if (!beforeStatus) {
    if (repoCapture === 'BEFORE_COMPLETED' || repoCapture === 'AFTER_COMPLETED' || repoCapture === 'AFTER_RUNNING') {
      beforeStatus = 'COMPLETED';
    } else if (repoCapture === 'BEFORE_FAILED') {
      beforeStatus = 'FAILED';
    } else if (repoCapture === 'RUNNING' || repoCapture === 'PENDING') {
      beforeStatus = repoCapture;
    }
  }
  let afterStatus = (capture?.after?.status || '').toUpperCase();
  if (!afterStatus) {
    if (repoCapture === 'AFTER_COMPLETED') afterStatus = 'COMPLETED';
    else if (repoCapture === 'AFTER_RUNNING') afterStatus = 'RUNNING';
    else if (repoCapture === 'AFTER_FAILED') afterStatus = 'FAILED';
    else if (beforeStatus === 'COMPLETED') afterStatus = 'NOT_READY';
  }
  const cmpStatus = (
    capture?.comparison?.status ||
    repository.comparisonStatus ||
    ''
  ).toUpperCase();
  const pr = (
    repository.prStatus ||
    repository.instrumentationPr?.status ||
    ''
  ).toUpperCase();

  if (beforeStatus === 'FAILED' || repoCapture === 'BEFORE_FAILED') {
    return 'BEFORE_CAPTURE_FAILED';
  }
  if (!beforeStatus || beforeStatus === 'PENDING' || beforeStatus === 'RUNNING') {
    return 'CAPTURE_PENDING';
  }

  if (pr === 'FAILED') return 'INSTRUMENTATION_FAILED';

  if (afterStatus === 'FAILED' || repoCapture === 'AFTER_FAILED') {
    return 'AFTER_CAPTURE_FAILED';
  }
  if (cmpStatus === 'FAILED') return 'COMPARE_FAILED';
  if (cmpStatus === 'RUNNING') return 'COMPARING';

  if (cmpStatus === 'COMPLETED') {
    // List cards often have only repository summary (no capture doc).
    const result = (
      capture?.comparison?.result ||
      repository.comparisonResult ||
      ''
    ).toUpperCase();
    if (result === 'PASS') return 'PASSED';
    if (
      result === 'FAIL' ||
      capture?.comparison?.hasDifferences === true
    ) {
      return 'HAS_DIFFERENCES';
    }
    // Completed compare without an explicit FAIL → treat as passed
    // (avoids red "differences" when the detail API shows PASS).
    if (!result || result === 'UNKNOWN') return 'PASSED';
    return 'FAILED';
  }

  if (afterStatus === 'PENDING' || afterStatus === 'RUNNING') {
    return 'CAPTURE_PENDING';
  }

  if (
    beforeStatus === 'COMPLETED' &&
    (!afterStatus || afterStatus === 'NOT_READY') &&
    ['OPEN', 'APPROVED', 'RAISED'].includes(pr)
  ) {
    return 'WAITING_FOR_PR';
  }

  if (
    beforeStatus === 'COMPLETED' &&
    pr === 'NOT_REQUIRED' &&
    (!afterStatus || afterStatus === 'NOT_READY' || afterStatus === 'PENDING')
  ) {
    return 'ALREADY_INSTRUMENTED';
  }

  if (
    beforeStatus === 'COMPLETED' &&
    pr === 'MERGED' &&
    (!afterStatus || afterStatus === 'NOT_READY' || afterStatus === 'PENDING')
  ) {
    return 'READY_TO_COMPARE';
  }

  if (
    beforeStatus === 'COMPLETED' &&
    afterStatus === 'COMPLETED' &&
    (!cmpStatus || cmpStatus === 'NOT_STARTED')
  ) {
    return 'READY_TO_COMPARE';
  }

  if (repoCapture === 'FAILED') return 'CAPTURE_FAILED';

  return 'CAPTURE_PENDING';
}
